import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2, Star, TrendingUp, ChevronDown, MapPin } from "lucide-react";
import { Reveal, ChapterHeading, MaskedLine, EASE } from "@/components/shared/Reveal";
import Marquee from "@/components/shared/Marquee";
import Counter from "@/components/shared/Counter";
import { api } from "@/lib/api";
import useSeo from "@/hooks/useSeo";
import Faq from "@/components/shared/Faq";
import { faqSchema, absUrl } from "@/lib/seo";
import { SERVICE_ID_TO_SLUG, SERVICE_PAGES } from "@/data/serviceDetails";
import { SERVICES, MARQUEE_ITEMS, STATS, PROCESS_STEPS, TESTIMONIALS, WHY_US, INDUSTRIES } from "@/data/content";

function HeroVisual() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const x1 = useTransform(sx, (v) => v * 26);
  const y1 = useTransform(sy, (v) => v * 20);
  const x2 = useTransform(sx, (v) => v * -18);
  const y2 = useTransform(sy, (v) => v * -14);
  const x3 = useTransform(sx, (v) => v * 12);
  const y3 = useTransform(sy, (v) => v * 16);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div ref={ref} onMouseMove={onMove} className="relative hidden lg:block h-[520px]" data-testid="hero-visual">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.9, ease: EASE }}
        style={{ x: x1, y: y1 }}
        className="absolute left-0 top-6 w-[380px] rounded-2xl border border-secondary/30 glass-dark p-5 shadow-2xl shadow-black/40 animate-float"
      >
        <div className="flex items-center justify-between">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
          </div>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Revenue Dashboard</span>
        </div>
        <div className="mt-5 flex h-28 items-end gap-2.5">
          {[38, 55, 42, 68, 52, 80, 64, 92, 74, 100].map((h, i) => (
            <motion.div
              key={i}
              initial={{ height: 0 }}
              animate={{ height: `${h}%` }}
              transition={{ duration: 0.8, delay: 1.3 + i * 0.06, ease: EASE }}
              className={`flex-1 rounded-t-md ${i % 3 === 0 ? "bg-pink-400/80" : "bg-blue-500/70"}`}
            />
          ))}
        </div>
        <div className="mt-4 flex justify-between border-t border-secondary/30 pt-3">
          <div><p className="text-[10px] uppercase tracking-widest text-slate-500">MRR</p><p className="font-display text-sm font-semibold text-slate-100">$128,400</p></div>
          <div><p className="text-[10px] uppercase tracking-widest text-slate-500">Growth</p><p className="font-display text-sm font-semibold text-emerald-400">+24.8%</p></div>
          <div><p className="text-[10px] uppercase tracking-widest text-slate-500">Churn</p><p className="font-display text-sm font-semibold text-slate-100">1.2%</p></div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.1, ease: EASE }}
        style={{ x: x2, y: y2 }}
        className="absolute right-0 top-40 w-[340px] rounded-2xl border border-secondary/30 bg-slate-900/90 p-5 shadow-2xl shadow-black/40 backdrop-blur-xl"
      >
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-3">automation.ts</p>
        <pre className="font-mono text-[11px] leading-relaxed">
          <code>
            <span className="text-pink-400">const</span> <span className="text-slate-200">workflow</span> <span className="text-slate-500">=</span> <span className="text-blue-400">await</span> <span className="text-slate-200">serpens</span><span className="text-slate-500">.</span><span className="text-emerald-400">automate</span><span className="text-slate-500">({"{"}</span>{"\n"}
            {"  "}<span className="text-slate-400">trigger:</span> <span className="text-amber-300">"order.created"</span><span className="text-slate-500">,</span>{"\n"}
            {"  "}<span className="text-slate-400">steps:</span> <span className="text-slate-500">[</span><span className="text-amber-300">"invoice"</span><span className="text-slate-500">,</span> <span className="text-amber-300">"dispatch"</span><span className="text-slate-500">,</span> <span className="text-amber-300">"notify"</span><span className="text-slate-500">],</span>{"\n"}
            {"  "}<span className="text-slate-400">ai:</span> <span className="text-pink-400">true</span>{"\n"}
            <span className="text-slate-500">{"}"});</span>
          </code>
        </pre>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.3, ease: EASE }}
        style={{ x: x3, y: y3 }}
        className="absolute bottom-4 left-16 w-[240px] rounded-2xl border border-secondary/30 glass-dark p-5 shadow-2xl shadow-black/40"
      >
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-400/15 text-emerald-400"><TrendingUp className="h-4 w-4" /></span>
          <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Efficiency</span>
        </div>
        <p className="mt-3 font-display text-3xl font-bold text-slate-50">+62%</p>
        <p className="text-xs text-slate-400">avg. operational gain for clients</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 1.5, ease: EASE }}
        style={{ x: x2, y: y3 }}
        className="absolute right-8 top-0 flex items-center gap-2.5 rounded-full border border-secondary/30 glass-dark px-4 py-2.5 shadow-xl"
      >
        <CheckCircle2 className="h-4 w-4 text-pink-400" />
        <span className="text-xs font-semibold text-slate-200">250+ Projects Delivered</span>
      </motion.div>
    </div>
  );
}

// Hero highlights are derived from STATS so numbers stay consistent across the page
const HERO_STATS = [
  ["Projects Delivered", "Projects"], ["Clients Worldwide", "Clients"], ["Countries Served", "Countries"],
].map(([label, short]) => {
  const s = STATS.find((x) => x.label === label);
  return [`${s.value}${s.suffix}`, short];
});

function Hero() {
  return (
    <section className="relative overflow-hidden bg-midnight" data-testid="hero-section">
      <div className="absolute inset-0 bg-grid-dark" />
      <div className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-blue-800/20 blur-[140px]" />
      <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-pink-500/15 blur-[140px]" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-8 pt-24 pb-20 md:pt-32 md:pb-28">
        <div className="grid items-center gap-16 lg:grid-cols-2">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05, ease: EASE }}
              className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-slate-700/80 glass-dark px-4 py-2"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-pink-400 animate-pulse-glow" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Global &amp; Domestic Digital Transformation Partner</span>
            </motion.div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.6rem] font-bold leading-[1.05] tracking-tighter text-slate-50" data-testid="hero-headline">
              <MaskedLine delay={0.1}>Custom Software &</MaskedLine>
              <MaskedLine delay={0.18}>Digital Solutions That</MaskedLine>
              <MaskedLine delay={0.26}><span className="text-gradient">Drive Business Growth</span></MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: EASE }}
              className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-slate-400"
              data-testid="hero-subheading"
            >
              Serpens Technology Pvt. Ltd. is a Bengaluru-based software development company helping startups, SMEs, and enterprises across India and worldwide build scalable custom software, AI-powered solutions, websites, mobile apps, and digital platforms that accelerate growth.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: EASE }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-start-project"
                className="group flex items-center gap-2.5 rounded-full bg-secondary px-7 py-3.5 text-sm font-semibold text-white shadow-xl shadow-blue-800/30 transition-all duration-300 hover:shadow-blue-800/50 hover:-translate-y-0.5 active:scale-95"
              >
                Start Your Project
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/portfolio"
                data-testid="hero-cta-view-portfolio"
                className="flex items-center gap-2.5 rounded-full border border-slate-600 px-7 py-3.5 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-pink-400/60 hover:text-pink-300 active:scale-95"
              >
                View Portfolio
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2.5"
              data-testid="hero-trust-strip"
            >
              {["Free discovery call", "NDA on request", "Reply within 24 hours"].map((t) => (
                <span key={t} className="flex items-center gap-2 text-xs font-medium text-slate-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-pink-400" /> {t}
                </span>
              ))}
              <span className="flex items-center gap-2 text-xs font-medium text-slate-400">
                <MapPin className="h-3.5 w-3.5 text-pink-400" /> HQ: Bengaluru, India — serving clients across India &amp; 15+ countries
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
            >
              {HERO_STATS.map(([v, l]) => (
                <div key={l}>
                  <p className="font-display text-2xl font-bold text-slate-50">{v}</p>
                  <p className="text-xs uppercase tracking-widest text-slate-500">{l}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <HeroVisual />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="mt-16 flex justify-center"
        >
          <div className="flex flex-col items-center gap-2 text-slate-500">
            <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
            <ChevronDown className="h-4 w-4 animate-bounce" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function ServicesBento() {
  const flagship = SERVICES[0];
  const rest = SERVICES.slice(1);
  return (
    <section className="py-24 md:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <ChapterHeading
          number="01"
          label="What We Do"
          title="Engineering capabilities that cover your entire digital journey"
          description="From flagship custom software to AI, cloud, and growth marketing — one accountable partner for everything digital."
        />
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          <Reveal className="md:col-span-2 lg:row-span-2">
            <Link
              to="/services/custom-software-development"
              data-testid="service-card-flagship"
              className="group relative flex h-full flex-col justify-between overflow-hidden rounded-3xl bg-midnight p-8 md:p-10 transition-transform duration-500 hover:-translate-y-1.5"
            >
              <div className="absolute inset-0 bg-grid-dark opacity-60" />
              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-800/25 blur-[100px] transition-opacity duration-500 group-hover:opacity-100" />
              <div className="relative">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-800 text-white">
                    <flagship.icon className="h-6 w-6" />
                  </div>
                  <span className="rounded-full bg-pink-400/15 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-pink-300">Flagship Service</span>
                </div>
                <h3 className="mt-6 font-display text-2xl md:text-3xl font-semibold tracking-tight text-slate-50">{flagship.title}</h3>
                <p className="mt-4 max-w-md text-sm md:text-base leading-relaxed text-slate-400">{flagship.description}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {flagship.subs.slice(0, 6).map((s) => (
                    <span key={s} className="rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-300">{s}</span>
                  ))}
                  <span className="rounded-full border border-pink-400/40 px-3 py-1 text-xs font-semibold text-pink-300">+24 more</span>
                </div>
              </div>
              <div className="relative mt-8 flex items-center gap-2 text-sm font-semibold text-pink-300">
                Explore custom software <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </div>
            </Link>
          </Reveal>
          {rest.map((s, i) => (
            <Reveal key={s.id} delay={0.08 * (i + 1)}>
              <Link
                to={`/services/${SERVICE_ID_TO_SLUG[s.id] || ""}`}
                data-testid={`service-card-${s.id}`}
                className="group flex h-full flex-col rounded-3xl border border-secondary/25 bg-card p-7 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl hover:shadow-accent/10"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-muted text-foreground transition-all duration-300 group-hover:bg-secondary group-hover:text-white group-hover:scale-110">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold tracking-tight">{s.title}</h3>
                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-muted-foreground line-clamp-3">{s.description}</p>
                <span className="mt-5 flex items-center gap-1.5 text-xs font-semibold text-secondary opacity-0 transition-all duration-300 group-hover:opacity-100">
                  Learn more <ArrowUpRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsBand() {
  return (
    <section className="border-y border-border/50 bg-card py-16 md:py-20" data-testid="stats-section">
      <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 px-6 md:grid-cols-5 md:px-8">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08} className="text-center">
            <p className="font-display text-4xl md:text-5xl font-bold tracking-tight text-gradient" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <Counter value={s.value} suffix={s.suffix} />
            </p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function ProcessTimeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.7", "end 0.6"] });
  return (
    <section className="py-24 md:py-32" data-testid="process-section">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <ChapterHeading
          number="02"
          label="How We Work"
          title="A proven process, refined over 250+ deliveries"
          description="Nine disciplined stages that turn an idea into reliable, scalable software — with full transparency at every step."
        />
        <div ref={ref} className="relative mx-auto max-w-4xl">
          <div className="absolute left-5 top-0 h-full w-px bg-border md:left-1/2" />
          <motion.div
            style={{ scaleY: scrollYProgress }}
            className="absolute left-5 top-0 h-full w-px origin-top bg-gradient-to-b from-blue-500 to-pink-400 md:left-1/2"
          />
          <div className="space-y-12 md:space-y-16">
            {PROCESS_STEPS.map((step, i) => (
              <Reveal key={step.title} delay={0.05}>
                <div className={`relative flex items-start gap-6 md:gap-0 ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}>
                  <div className={`hidden md:block md:w-1/2 ${i % 2 === 0 ? "md:pr-16 md:text-right" : "md:pl-16"}`}>
                    <h3 className="font-display text-xl font-semibold tracking-tight">{step.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.description}</p>
                  </div>
                  <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-accent/40 bg-card shadow-lg shadow-accent/10 md:absolute md:left-1/2 md:-translate-x-1/2">
                    <step.icon className="h-4 w-4 text-accent" />
                  </div>
                  <div className="md:w-1/2 md:hidden">
                    <h3 className="font-display text-lg font-semibold tracking-tight">{step.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{step.description}</p>
                  </div>
                  <div className={`hidden md:block md:w-1/2 ${i % 2 === 0 ? "md:pl-16" : "md:pr-16 md:text-right"}`}>
                    <span className="font-display text-5xl font-bold text-muted/80">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PortfolioPreview() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.portfolioList().then((r) => setItems(r.data.slice(0, 3))).catch(() => setItems([]));
  }, []);
  return (
    <section className="bg-midnight py-24 md:py-32" data-testid="portfolio-preview-section">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <ChapterHeading
            dark
            number="03"
            label="Selected Work"
            title="Software that moved the numbers that matter"
          />
          <Reveal delay={0.2} className="mb-14 md:mb-20">
            <Link to="/portfolio" data-testid="portfolio-view-all" className="group flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-pink-400/60 hover:text-pink-300">
              View all work <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {items.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <Link to="/portfolio" data-testid={`portfolio-preview-${p.id}`} className="group block overflow-hidden rounded-3xl border border-secondary/25 bg-slate-900/60 transition-all duration-500 hover:-translate-y-2 hover:border-secondary/50">
                <div className="relative h-52 overflow-hidden">
                  {p.image ? (
                    <img src={p.image} alt={`${p.title} — ${p.type} project by Serpens Technology`} loading="lazy" decoding="async" width="600" height="300" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-midnight">
                      <span className="font-display text-4xl font-bold text-gradient">{p.title.slice(0, 2).toUpperCase()}</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                  <span className="absolute left-4 top-4 rounded-full glass-dark px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-pink-300">{p.industry}</span>
                </div>
                <div className="p-6">
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{p.type}</p>
                  <h3 className="mt-2 font-display text-xl font-semibold text-slate-50">{p.title}</h3>
                  <p className="mt-2.5 flex items-center gap-2 text-sm font-medium text-emerald-400">
                    <TrendingUp className="h-4 w-4" /> {p.outcomes[0]}
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="py-24 md:py-32" data-testid="testimonials-section">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <ChapterHeading
          number="04"
          label="Client Stories"
          title="Trusted by leaders who measure results"
          description="Real partnerships, measurable outcomes. Here is what working with Serpens feels like."
        />
        <div className="grid gap-6 md:grid-cols-2">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div className="flex h-full flex-col rounded-3xl border border-secondary/25 bg-card p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-accent/10" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1">
                  {Array.from({ length: t.rating }).map((_, s) => (
                    <Star key={s} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <blockquote className="mt-5 flex-1 text-base leading-relaxed text-foreground/90">“{t.quote}”</blockquote>
                <div className="mt-6 flex items-center justify-between border-t border-border/50 pt-5">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-blue-800 to-pink-500 font-display text-sm font-bold text-white">
                      {t.name.split(" ").map((n) => n[0]).join("")}
                    </div>
                    <div>
                      <p className="text-sm font-semibold">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.role}</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-500">{t.result}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyUs() {
  return (
    <section className="border-t border-border/50 bg-card py-24 md:py-32" data-testid="why-us-section">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <ChapterHeading
          number="05"
          label="Why Serpens"
          title="The partner enterprises trust and startups afford"
        />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
          {WHY_US.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.05}>
              <div className="group h-full rounded-2xl border border-secondary/25 bg-background p-6 transition-all duration-300 hover:-translate-y-1 hover:border-accent/50" data-testid={`why-us-${i}`}>
                <w.icon className="h-6 w-6 text-accent transition-transform duration-300 group-hover:scale-110" />
                <h3 className="mt-4 text-sm font-semibold leading-snug">{w.title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{w.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-16 flex flex-wrap gap-3">
            {INDUSTRIES.map((ind) => (
              <span key={ind.name} className="flex items-center gap-2 rounded-full border border-secondary/25 bg-background px-4 py-2 text-xs font-medium text-muted-foreground transition-colors duration-200 hover:border-accent/50 hover:text-foreground">
                <ind.icon className="h-3.5 w-3.5 text-accent" /> {ind.name}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export const HOME_FAQS = [
  { q: "What does Serpens Technology do?", a: "Serpens Technology Pvt. Ltd. is a software development company that builds custom software, ERP and CRM systems, websites, eCommerce stores, mobile apps and AI-powered automation for startups, SMEs and enterprises." },
  { q: "Where is Serpens Technology located?", a: "Our headquarters is at 36, 2nd Main Pipeline Road, Mathikere, Bengaluru, Karnataka 560054, India. We work with clients in Bengaluru, across India and internationally." },
  { q: "Do you work with clients outside Bangalore and India?", a: "Yes. As a global and domestic digital transformation partner, we deliver projects remotely for clients across India and in other countries, with shared dashboards, weekly demos and overlapping working hours." },
  { q: "Which technologies does Serpens Technology use?", a: "We work with React, Next.js, Node.js, Python, Java, PHP/Laravel, Flutter, React Native, WordPress, Shopify, PostgreSQL, MongoDB, Firebase, AWS, Google Cloud, OpenAI and LangChain, choosing the stack that fits each project." },
  { q: "How do I start a project with Serpens Technology?", a: "Book a free discovery call through our contact page, WhatsApp or phone (+91 82170 64852). We map your requirements and share an honest estimate, usually within 48 hours." },
];

function AtAGlance() {
  const facts = [
    ["What we do", "Custom software, ERP & CRM, web & eCommerce development, mobile apps, AI & automation and UI/UX design."],
    ["Who we serve", "Startups, small and medium businesses, and enterprises undergoing digital transformation."],
    ["Where we work", "Headquartered in Bengaluru, Karnataka — serving clients across India and internationally."],
    ["How we deliver", "Discovery, fixed itemised proposals, agile sprints with weekly demos, and long-term support. You own the code."],
  ];
  return (
    <section className="py-24 md:py-32" data-testid="at-a-glance-section">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <ChapterHeading
          number="06"
          label="At a Glance"
          title="Software development company in Bangalore, partnering globally"
          description="Serpens Technology is a global and domestic digital transformation partner — engineering from Bengaluru for businesses in India and around the world."
        />
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            <dl className="grid gap-4 sm:grid-cols-2">
              {facts.map(([k, v], i) => (
                <Reveal key={k} delay={i * 0.05}>
                  <div className="h-full rounded-2xl border border-secondary/25 bg-card p-6">
                    <dt className="text-xs font-bold uppercase tracking-widest text-accent">{k}</dt>
                    <dd className="mt-3 text-sm leading-relaxed text-muted-foreground">{v}</dd>
                  </div>
                </Reveal>
              ))}
            </dl>
            <Reveal delay={0.2}>
              <p className="mt-8 text-xs font-bold uppercase tracking-widest text-muted-foreground">Explore our services</p>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {SERVICE_PAGES.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} className="rounded-full border border-secondary/25 bg-card px-4 py-2 text-xs font-semibold transition-colors duration-200 hover:border-accent/50 hover:text-accent">
                    {s.name}
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
          <div>
            <h3 className="mb-6 font-display text-2xl font-semibold tracking-tight">Frequently asked questions</h3>
            <Faq items={HOME_FAQS} />
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="relative overflow-hidden bg-midnight py-24 md:py-32" data-testid="cta-section">
      <div className="absolute inset-0 bg-grid-dark" />
      <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-800/20 blur-[120px]" />
      <div className="relative mx-auto max-w-4xl px-6 text-center md:px-8">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-50">
            Have an idea? <span className="text-gradient">Let's engineer it.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mx-auto mt-6 max-w-xl text-base md:text-lg text-slate-400">
            Book a free discovery call. We will map your requirements, suggest the right architecture, and give you an honest estimate — within 48 hours.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="cta-start-project" className="group flex items-center gap-2.5 rounded-full bg-secondary px-8 py-4 text-sm font-semibold text-white shadow-xl shadow-blue-800/30 transition-all duration-300 hover:shadow-blue-800/50 hover:-translate-y-0.5 active:scale-95">
              Start Your Project <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/contact" data-testid="cta-book-call" className="rounded-full border border-slate-600 px-8 py-4 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-pink-400/60 hover:text-pink-300">
              Book a Discovery Call
            </Link>
            <Link to="/services" data-testid="cta-explore-services" className="rounded-full border border-slate-600 px-8 py-4 text-sm font-semibold text-slate-200 transition-all duration-300 hover:border-pink-400/60 hover:text-pink-300">
              Explore Services
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  useSeo(
    "Software Development Company in Bangalore | Serpens Technology",
    "Serpens Technology is a software development company in Bangalore building custom software, ERP & CRM, websites, eCommerce, mobile apps and AI solutions for clients across India and worldwide.",
    { schema: [faqSchema(HOME_FAQS), { "@type": "ItemList", name: "Services", itemListElement: SERVICE_PAGES.map((s, i) => ({ "@type": "ListItem", position: i + 1, name: s.name, url: absUrl(`/services/${s.slug}`) })) }], pageType: "WebPage" }
  );
  return (
    <>
      <Hero />
      <div className="border-b border-border/50 py-8">
        <Marquee items={MARQUEE_ITEMS} />
      </div>
      <ServicesBento />
      <StatsBand />
      <ProcessTimeline />
      <PortfolioPreview />
      <Testimonials />
      <WhyUs />
      <AtAGlance />
      <CtaBand />
    </>
  );
}
